// apps/web/src/routes/aksi-massal/pilihanRiwayat.ts
/**
 * The ticked rows a print sheet lists (spec 2026-10-02-aksi-massal, 5): handed over in the
 * sheet's history entry with whose they are, never in the address (a long list of ids has no
 * place there), and read back only for that person: on a shared device another person's entry,
 * reached by Back, shows nothing of theirs. The entry's other keys (the search of
 * `useKueriRiwayat`) stay.
 *
 * A selection's sheet says so in its address (`pilih=1`, which names no one), because its
 * address is otherwise a whole list's: without its ids for this person (another person's entry,
 * an entry the ids did not reach) it lists nothing, never everything its filters find
 * (`pilihanLembar`; the print route refuses it). The lock carries the same person's ids to the
 * PIN screen and back with the search (`bawaPilihan`, auth/guard). Every list's selection sheet
 * (Daftar Umat, Keluarga, a register book, letters) uses these. Pure; relative imports.
 */

/** The address marker of a selection's sheet: `pilih=1`. */
export const TANDA_PILIHAN = 'pilih'

export function denganPilihan(
  keadaan: Record<string, unknown> | null,
  ids: readonly string[],
  pengguna: string,
): Record<string, unknown> {
  return { ...(keadaan ?? {}), pilihan: [...ids], pilihanUntuk: pengguna }
}

/** The ids the entry holds for `pengguna`; null when it holds none for them. */
export function pilihanDariKeadaan(keadaan: unknown, pengguna: string): string[] | null {
  if (typeof keadaan !== 'object' || keadaan === null) return null
  const { pilihan, pilihanUntuk } = keadaan as { pilihan?: unknown; pilihanUntuk?: unknown }
  if (pilihanUntuk !== pengguna || !Array.isArray(pilihan)) return null
  return pilihan.filter((v): v is string => typeof v === 'string')
}

/**
 * A sheet's address (`/cetak/…?…`) marked as a selection's (`dipilih`), or not: a whole list's
 * never keeps a marker its list's own address happened to carry.
 */
export function alamatLembar(alamat: string, dipilih: boolean): string {
  const tanya = alamat.indexOf('?')
  const jalur = tanya === -1 ? alamat : alamat.slice(0, tanya)
  const params = new URLSearchParams(tanya === -1 ? '' : alamat.slice(tanya + 1))
  if (dipilih) params.set(TANDA_PILIHAN, '1')
  else params.delete(TANDA_PILIHAN)
  const cari = params.toString()
  return cari === '' ? jalur : `${jalur}?${cari}`
}

export type PilihanLembar =
  /** No marker: the sheet lists by its address alone. */
  | { jenis: 'alamat' }
  /** A selection's sheet with this person's ticked ids. */
  | { jenis: 'pilihan'; ids: string[] }
  /** A selection's sheet whose ids are not this person's or did not come along: nothing. */
  | { jenis: 'hilang' }

/** What a sheet at `search` lists for `pengguna`, from its marker and its entry's state. */
export function pilihanLembar(
  search: URLSearchParams,
  keadaan: unknown,
  pengguna: string,
): PilihanLembar {
  if (search.get(TANDA_PILIHAN) !== '1') return { jenis: 'alamat' }
  const ids = pilihanDariKeadaan(keadaan, pengguna)
  return ids === null ? { jenis: 'hilang' } : { jenis: 'pilihan', ids }
}

/**
 * `ke` (the state the lock hands on, auth/guard) with the ids `dari` (the locked entry's state)
 * holds for `pengguna`; unchanged when it holds none of theirs.
 */
export function bawaPilihan(
  dari: unknown,
  ke: Record<string, unknown> | null,
  pengguna: string,
): Record<string, unknown> | null {
  const ids = pilihanDariKeadaan(dari, pengguna)
  return ids === null ? ke : denganPilihan(ke, ids, pengguna)
}
